import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';

import { ScheduleDiffEl, ScheduleDiffTypes } from '@mopla/business-logic';

import { MplButton } from '../../atoms/MplButton/MplButton';
import { MplModal } from '../../organisms/MplModal/MplModal';

import TripCard from './TripCard';
import { filterAndSortByUpdateState } from './utils';

interface DistributorNotificationModalProps {
	onClose: () => void;
	onSubmit: () => void;
	showModal: boolean;
	legsDiff: ScheduleDiffEl[] | null;
}

export const DistributorNotificationModal: FC<
	DistributorNotificationModalProps
> = ({ onClose, onSubmit, showModal, legsDiff }) => {
	const { t } = useTranslation('notifications');

	const addedRides = useMemo(
		() => filterAndSortByUpdateState(legsDiff, ScheduleDiffTypes.NEW),
		[legsDiff]
	);
	const canceledRides = useMemo(
		() => filterAndSortByUpdateState(legsDiff, ScheduleDiffTypes.DELETED),
		[legsDiff]
	);
	const delayedRides = useMemo(
		() => filterAndSortByUpdateState(legsDiff, ScheduleDiffTypes.DELAYED),
		[legsDiff]
	);
	const breakStop = useMemo(
		() =>
			filterAndSortByUpdateState(
				legsDiff,
				ScheduleDiffTypes.NEW_BREAK,
				ScheduleDiffTypes.DELAYED_BREAK
			),
		[legsDiff]
	);

	return (
		<MplModal
			open={showModal}
			onClose={onClose}
			title={t('text.changes_in_roster')}
			testId="DistributorNotificationModal-MplModal"
			content={
				<Stack width="100%" gap={3}>
					{Boolean(addedRides?.length) && (
						<Stack
							gap={2}
							data-testid="distributorNotificationModal-addedRides-section"
						>
							<Typography variant="body1" fontWeight={600}>
								{t('text.added_rides')}
							</Typography>
							<Stack gap={1.5}>
								{addedRides?.map((leg) => (
									<TripCard leg={leg} key={leg.scheduledLegId} />
								))}
							</Stack>
						</Stack>
					)}
					{Boolean(canceledRides?.length) && (
						<Stack
							gap={2}
							data-testid="distributorNotificationModal-canceledRides-section"
						>
							<Typography variant="body1" fontWeight={600}>
								{t('text.canceled_trips')}
							</Typography>
							<Stack gap={1.5}>
								{canceledRides?.map((leg) => (
									<TripCard leg={leg} key={leg.scheduledLegId} />
								))}
							</Stack>
						</Stack>
					)}
					{Boolean(delayedRides?.length) && (
						<Stack
							gap={2}
							data-testid="distributorNotificationModal-delayedRides-section"
						>
							<Typography variant="body1" fontWeight={600}>
								{t('text.delayed_rides')}
							</Typography>
							<Stack gap={1.5}>
								{delayedRides?.map((leg) => (
									<TripCard leg={leg} key={leg.scheduledLegId} />
								))}
							</Stack>
						</Stack>
					)}
					{Boolean(breakStop?.length) && (
						<Stack
							gap={2}
							data-testid="distributorNotificationModal-break-section"
						>
							<Typography variant="body1" fontWeight={600}>
								{t('text.pause')}
							</Typography>
							<Stack gap={1.5}>
								{breakStop?.map((leg) => (
									<TripCard leg={leg} key={leg.scheduledLegId} />
								))}
							</Stack>
						</Stack>
					)}
				</Stack>
			}
			buttons={
				<MplButton fullWidth onClick={onSubmit}>
					{t('button.back_to_home')}
				</MplButton>
			}
		/>
	);
};

export default DistributorNotificationModal;
